'use client';

import { createContext, type ReactNode, useCallback, useContext, useMemo, useRef, useState } from 'react';
import { type EarnMyApply, getMyApply } from '../_apis';

type EarnApplyStatus = 'not_applied' | 'reviewing' | 'rejected' | 'approved';
type EarnStatusLoadState = 'idle' | 'loading' | 'success' | 'error';

type EarnStatusContextValue = {
  error?: unknown;
  loadState: EarnStatusLoadState;
  myApply?: EarnMyApply;
  status?: EarnApplyStatus;
  refreshStatus: (options?: { force?: boolean }) => Promise<EarnMyApply>;
  setMyApply: (myApply: EarnMyApply) => void;
  setStatus: (status: EarnApplyStatus) => void;
};

const EarnStatusContext = createContext<EarnStatusContextValue | undefined>(undefined);

export function EarnStatusProvider({ children }: { children: ReactNode }) {
  const [myApply, setMyApplyState] = useState<EarnMyApply>();
  const [status, setStatus] = useState<EarnApplyStatus>();
  const [loadState, setLoadState] = useState<EarnStatusLoadState>('idle');
  const [error, setError] = useState<unknown>();
  const myApplyRef = useRef<EarnMyApply | undefined>(undefined);
  const statusRef = useRef<EarnApplyStatus | undefined>(undefined);
  const pendingRequestRef = useRef<Promise<EarnMyApply> | null>(null);

  const updateMyApply = useCallback((nextMyApply: EarnMyApply) => {
    myApplyRef.current = nextMyApply;
    statusRef.current = nextMyApply.status;
    setMyApplyState(nextMyApply);
    setStatus(nextMyApply.status);
  }, []);

  const updateStatus = useCallback((nextStatus: EarnApplyStatus) => {
    statusRef.current = nextStatus;
    setStatus(nextStatus);
    if (myApplyRef.current) {
      const nextMyApply = { ...myApplyRef.current, status: nextStatus };
      myApplyRef.current = nextMyApply;
      setMyApplyState(nextMyApply);
    }
  }, []);

  const refreshStatus = useCallback(
    async ({ force = false }: { force?: boolean } = {}) => {
      if (!force && myApplyRef.current) return myApplyRef.current;
      if (!force && pendingRequestRef.current) return pendingRequestRef.current;

      const request = getMyApply();
      pendingRequestRef.current = request;
      setLoadState('loading');
      setError(undefined);

      try {
        const nextMyApply = await request;
        updateMyApply(nextMyApply);
        setLoadState('success');
        return nextMyApply;
      } catch (nextError) {
        setError(nextError);
        setLoadState('error');
        throw nextError;
      } finally {
        if (pendingRequestRef.current === request) {
          pendingRequestRef.current = null;
        }
      }
    },
    [updateMyApply],
  );

  const value = useMemo(
    () => ({ error, loadState, myApply, refreshStatus, setMyApply: updateMyApply, setStatus: updateStatus, status }),
    [error, loadState, myApply, refreshStatus, status, updateMyApply, updateStatus],
  );

  return <EarnStatusContext.Provider value={value}>{children}</EarnStatusContext.Provider>;
}

export function useEarnStatus() {
  const context = useContext(EarnStatusContext);

  if (!context) {
    throw new Error('useEarnStatus must be used within EarnStatusProvider');
  }

  return context;
}
